import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { fetchServices } from '../lib/cms';
import { SITE_INFO } from '../data/siteData';
import InitialConsultationCallout from '../components/InitialConsultationCallout';

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    async function load() {
      const data = await fetchServices();
      // Sort in desired order if order exists
      const sorted = [...data].sort((a, b) => (a.order || 99) - (b.order || 99));
      setServices(sorted);
    }
    load();
  }, []);

  const filteredServices = activeFilter === 'all'
    ? services
    : services.filter(s => {
        const cat = s.kategori || s.terms?.forloebskategori?.[0] || '';
        if (cat) {
          if (activeFilter === 'unge') return cat === 'unge';
          if (activeFilter === 'voksen') return cat === 'voksne' || cat === 'voksen';
          if (activeFilter === 'autisme') return cat === 'autisme-adhd' || cat === 'autisme';
          if (activeFilter === 'foraeldre') return cat === 'foraeldre-raadgivning' || cat === 'foraeldre';
        }
        const title = (s.title || s.titel || '').toLowerCase();
        const slug = (s.custom_slug || s.brugerdefineret_slug || '').toLowerCase();
        if (activeFilter === 'unge') return title.includes('unge') || slug.includes('unge');
        if (activeFilter === 'voksen') return title.includes('voksen') || slug.includes('voksen');
        if (activeFilter === 'autisme') return title.includes('autisme') || slug.includes('autisme') || title.includes('adhd');
        if (activeFilter === 'foraeldre') return title.includes('forældre') || slug.includes('foraeldre') || title.includes('rådgivning');
        return true;
      });

  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        title="mistrivsel, angst mentor - KerneViden"
        description="Få mere at vide om, hvilket forløb der passer til dig."
        ogTitle="mistrivsel, angst mentor"
        ogDescription="Få mere at vide om, hvilket forløb der passer til dig."
        ogImage="https://assets.auxodev.dk/assets/1d30bca3-a748-4ede-8335-828014d68a29/fb07814f-6271-41bf-b601-ea8f1704b3f8_f7175f1d.jpeg"
      />

      {/* Page Header */}
      <section className="pt-14 pb-12 bg-white border-b border-[#EAE2D7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#233424] font-normal tracking-tight">
            Forløb tilpasset dig.
          </h1>
          <p className="font-serif text-lg sm:text-xl text-[#526051] italic max-w-2xl mx-auto leading-relaxed">
            Få mere at vide om, hvilket forløb der passer til dig og din situation.
          </p>

          {/* Quick Filter Navigation */}
          <div className="flex flex-wrap items-center justify-center gap-2 pt-4">
            <button
              type="button"
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                activeFilter === 'all'
                  ? 'bg-[#233424] text-white shadow-sm border border-[#233424]'
                  : 'bg-white text-[#233424] hover:bg-white border border-[#233424] hover:scale-105 active:scale-95'
              }`}
            >
              Alle forløb
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('unge')}
              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                activeFilter === 'unge'
                  ? 'bg-[#233424] text-white shadow-sm border border-[#233424]'
                  : 'bg-white text-[#233424] hover:bg-white border border-[#233424] hover:scale-105 active:scale-95'
              }`}
            >
              Ungeforløb
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('voksen')}
              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                activeFilter === 'voksen'
                  ? 'bg-[#233424] text-white shadow-sm border border-[#233424]'
                  : 'bg-white text-[#233424] hover:bg-white border border-[#233424] hover:scale-105 active:scale-95'
              }`}
            >
              Voksenforløb
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('autisme')}
              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                activeFilter === 'autisme'
                  ? 'bg-[#233424] text-white shadow-sm border border-[#233424]'
                  : 'bg-white text-[#233424] hover:bg-white border border-[#233424] hover:scale-105 active:scale-95'
              }`}
            >
              Autisme &amp; ADHD
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('foraeldre')}
              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                activeFilter === 'foraeldre'
                  ? 'bg-[#233424] text-white shadow-sm border border-[#233424]'
                  : 'bg-white text-[#233424] hover:bg-white border border-[#233424] hover:scale-105 active:scale-95'
              }`}
            >
              Forældrerådgivning
            </button>
          </div>
        </div>
      </section>

      {/* Services List Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {filteredServices.map((service, index) => {
            return (
              <article
                key={service.id || service.custom_slug || index}
                id={service.custom_slug}
                className="bg-white rounded-3xl p-6 sm:p-8 lg:p-10 shadow-[0_16px_45px_rgba(44,62,45,0.06)] hover:shadow-[0_22px_55px_rgba(44,62,45,0.10)] transition-all duration-300 flex flex-col justify-between space-y-6 scroll-mt-28"
              >
                <div className="space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-[#F0EAE1]">
                    <div>
                      {service.subtitle && (
                        <span className="text-xs font-semibold uppercase tracking-wider text-[#8A9887] block mb-1">
                          {service.subtitle}
                        </span>
                      )}
                      <h2 className="font-serif text-2xl sm:text-3xl text-[#233424] font-medium tracking-tight">
                        {service.title}
                      </h2>
                    </div>

                    {service.price && (
                      <div className="self-start sm:self-center text-sm sm:text-base font-semibold text-[#233424] whitespace-nowrap">
                        {service.price}
                      </div>
                    )}
                  </div>

                  {service.quote && (
                    <blockquote className="p-4 rounded-2xl bg-[#EFEFEF] border-l-3 border-[#233424] text-sm italic text-[#2D382D] font-serif leading-relaxed">
                      {service.quote}
                    </blockquote>
                  )}

                  <div
                    className="text-sm sm:text-base leading-relaxed text-[#455244] space-y-3 prose prose-slate max-w-none [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-1.5 [&>ul>li]:text-sm [&>p]:mb-2.5 font-normal"
                    dangerouslySetInnerHTML={{ __html: service.description }}
                  />
                </div>

                {/* Card Bottom CTA Bar */}
                <div className="pt-6 border-t border-[#F0EAE1] flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-xs text-[#6B7C69]">
                    <i className="fa-regular fa-circle-check text-[#3A4F3D]" aria-hidden="true" />
                    <span>Gratis og uforpligtende samtale</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href={`tel:${SITE_INFO.phone.replace(/\s+/g, '')}`}
                      className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-xs font-medium text-[#233424] bg-[#FAF7F2] hover:bg-[#EFEAE2] transition-colors whitespace-nowrap"
                    >
                      <i className="fa-regular fa-phone mr-1.5 text-[#233424]" aria-hidden="true" />
                      <span>Ring</span>
                    </a>

                    <Link
                      to="/kontakt"
                      className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-medium text-white bg-[#3A4F3D] hover:bg-[#233424] shadow-xs transition-all duration-200 whitespace-nowrap group hover:scale-105 active:scale-95"
                    >
                      <span>Book samtale</span>
                      <i className="fa-solid fa-arrow-right ml-2 text-xs text-[#E3C2B0] transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Reusable Initial Consultation Callout */}
      <InitialConsultationCallout
        title="Der tilbydes en indledende telefonisk/online samtale gratis."
        description="Få mere at vide om, hvilket forløb der passer til dig, og hvordan jeg kan skabe trygge rammer for din udvikling."
        ctaText="Kontakt KerneViden"
      />
    </div>
  );
}
